import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Grid2, Typography, Tab, Tabs, Box, IconButton, Alert, SxProps, useTheme, Container, alpha, Tooltip, TextField, Button, Snackbar } from '@mui/material';
import { Favorite as FavoriteIcon, FavoriteBorder as FavoriteBorderIcon } from '@mui/icons-material';
import PageWrapper from '../../components/PageWrapper';
import useAuth, { useAuthEffects } from '../../components/Auth';
import { usePlanGetInfoPublicQuery, usePlanGetInfoQuery, usePlanGetInfoPublicWithPasswordMutation } from '../../services/plans';
import { PlanViewResourcesTabContent } from './components/PlanViewResourcesTabContent';
import { FlashingFavouriteIconButtonWithRegisterTooltip } from '../../components/FavouriteButton';
import { PlanListItemSource } from '../../types';
import ytImage from '../../assets/yt_logo_rgb_light.png';
import ytImageDark from '../../assets/yt_logo_rgb_dark.png';
import igImage from '../../assets/ig_glyph_black.svg';
import igImageDark from '../../assets/ig_glyph_white.svg';
import pinterestImage from '../../assets/pinterest_red.svg';
import pinterestImageDark from '../../assets/pinterest_white.svg';
import tiktokImage from '../../assets/tiktok.png';
import { Trans, useTranslation } from 'react-i18next';
import { PlanViewInfoTabContent } from './components/PlanViewInfoTabContent';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import EditIcon from '@mui/icons-material/Edit';
import { trackPixelCustomEvent, trackGAEvent } from '../../common/analytics';
import WishlistDialog, { useWishlistLogic } from '../discover/components/WishlistDialog';
import { SEOHead } from '../../components/SEOHead';
import { generateProductSchema, generateHowToSchema, generateBreadcrumbSchema } from '../../utils/structuredData';
import { useQuickOrdersListByPlanIdPublicQuery, useQuickOrdersListQuery } from '../../services/quickOrders';
import { usePlanAdsListQuery, usePlanAdslistByPlanIdPublicQuery } from '../../services/planAds';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { useShippingRegionContext } from '../../contexts/ShippingRegionContext';
import { useFavourites } from '../../contexts/FavouritesContext';
import { useAppSelector } from '../../hooks';
import { selectMyCompany } from '../accountSlice';

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

function CustomTabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props;
	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
		>
			{value === index && <Box sx={{ mt: 3 }}>{children}</Box>}
		</div>
	);
}

function a11yProps(index: number) {
	return {
		id: `simple-tab-${index}`,
		'aria-controls': `simple-tabpanel-${index}`,
	};
}

export const PlanAuthorCaption = ({ authorName, source, sx }: { authorName: string, source: PlanListItemSource | null, sx?: SxProps }) => {
	const theme = useTheme();

	if (source === PlanListItemSource.Youtube) {
		return <Box display="flex" alignItems="center" gap={1} sx={sx}>
			<Typography variant='caption'>
				<Trans 
					i18nKey='planView.contentFromYoutubeByAuthor'
					values={{ authorName }}
					components={{ 
						sourceIcon: <img src={theme.palette.mode === 'dark' ? ytImageDark : ytImage} alt="YouTube" style={{height: '14px', marginBottom: '-2px'}} />, 
					}} 
				/>
			</Typography>
		</Box>;
	}
	if (source === PlanListItemSource.Instagram) {
		return <Box display="flex" alignItems="center" gap={1} sx={sx}>
			<Typography variant='caption'>
				<Trans 
					i18nKey='planView.contentFromInstagramByAuthor'
					values={{ authorName }}
					components={{ 
						sourceIcon: <img src={theme.palette.mode === 'dark' ? igImageDark : igImage} alt="Instagram" style={{height: '18px', marginBottom: '-4px'}} />, 
					}} 
				/>
			</Typography>
		</Box>;
	}
	if (source === PlanListItemSource.Pinterest) {
		return <Box display="flex" alignItems="center" gap={1} sx={sx}>
			<Typography variant='caption'>
				<Trans 
					i18nKey='planView.contentFromPinterestByAuthor'
					values={{ authorName }}
					components={{ 
						sourceIcon: <img src={theme.palette.mode === 'dark' ? pinterestImageDark : pinterestImage} alt="Pinterest" style={{height: '18px', marginBottom: '-4px'}} />, 
					}} 
				/>
			</Typography>
		</Box>;
	}
	if (source === PlanListItemSource.TikTok) {
		return <Box display="flex" alignItems="center" gap={1} sx={sx}>
			<Typography variant='caption'>
				<Trans i18nKey='planView.contentFromTikTokByAuthor' values={{ authorName }} components={{ 
					sourceIcon: <img src={tiktokImage} alt="TikTok" style={{height: '18px', marginBottom: '-4px'}} />, 
				}} />
			</Typography>
		</Box>;
	}

	return <Typography variant='caption' sx={{ '& .MuiImageListItemBar-titleWrap': { color: 'inherit' } }}>
		<Trans 
			i18nKey='planView.byAuthor'
			values={{ authorName }}
			components={{ 
				// sourceIcon: <img src={theme.palette.mode === 'dark' ? diyfeedImageDark : diyfeedImage} alt="DIYFeed" style={{height: '24px', marginBottom: '-8px'}} />, 
				sourceIcon: <span></span>,
			}} 
		/>
	</Typography>;
};

const PlanView = ({ isSuppliesTab = false }: { isSuppliesTab?: boolean }) => {
	useAuthEffects();

	const theme = useTheme();
	const materialsListRef = useRef<any>(null);
	const { id } = useParams();
	const {userProfile, isAuthLoading} = useAuth();
	const companyProfile = useAppSelector(selectMyCompany);
	const { selectedRegion } = useShippingRegionContext();
	const {data: planInfoPublic, error: planInfoPublicError} = usePlanGetInfoPublicQuery({id: id as string, countryCode: selectedRegion as string}, {skip: isAuthLoading || !id || Boolean(userProfile) || !selectedRegion});
	const {data: planInfo, error: planInfoError} = usePlanGetInfoQuery({id: id as string, countryCode: selectedRegion as string}, {skip: isAuthLoading || !id || !userProfile || !selectedRegion});
	const [planGetInfoPublicWithPassword, {data: planInfoPublicWithPassword}] = usePlanGetInfoPublicWithPasswordMutation();
	const { data: planShoppingLists = [], isLoading: isPlanShoppingListsLoading } = useQuickOrdersListByPlanIdPublicQuery({planId: id as string, countryCode: selectedRegion as string}, {skip: !id || !selectedRegion});
	const { data: companyShoppingLists = [], isLoading: isCompanyShoppingListsLoading } = useQuickOrdersListQuery(undefined, {skip: !id || !companyProfile?.roleVendor});
	const { data: companyPlanAds = [] } = usePlanAdsListQuery(undefined, {skip: !companyProfile?.roleVendor});
	const { data: planAds = [], isLoading: isPlanAdsLoading } = usePlanAdslistByPlanIdPublicQuery(id as string, { skip: !id });
	const { handleAddFavouriteBtnClick, handleRemoveFavouriteBtnClick, addToWishlistPlanInfo, setAddToWishlistPlanInfo } = useWishlistLogic();
	const {favouriteIds} = useFavourites();
	const [isLoading, setIsLoading] = useState(true);
	const [isPasswordRequired, setIsPasswordRequired] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const navigate = useNavigate();
	const { t } = useTranslation();
	const companyShoppingList = useMemo(() => companyShoppingLists.find(list => list.planId === Number(id)) || null, [companyShoppingLists, id]);
	const companyPlanAd = useMemo(() => companyPlanAds.find(ad => ad.planId === Number(id)) || null, [companyPlanAds, id]);	

	const plan = useMemo(() => userProfile ? planInfo : planInfoPublicWithPassword || planInfoPublic, [userProfile, planInfo, planInfoPublic, planInfoPublicWithPassword]);

	useEffect(() => {
		if (plan?.passwordRequired) {
			setIsPasswordRequired(true);
		}
	}, [plan?.passwordRequired]);

	const handlePasswordSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const formData = new FormData(event.target as HTMLFormElement);
		const password = formData.get('password') as string;
		planGetInfoPublicWithPassword({id: id as string, password}).unwrap().then((data) => {
			if (data.id) {
				setIsPasswordRequired(false);
			} else {
				setErrorMessage(t('planView.invalidPassword'));
			}
		});
	};

	const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
		if (newValue === 1) {
			navigate(`/plans/${id}/supplies`);
		} else {
			navigate(`/plans/${id}`);
		}
	};

	useEffect(() => {
		if (isPlanShoppingListsLoading || isCompanyShoppingListsLoading || isPlanAdsLoading || isAuthLoading || !plan) {
			setIsLoading(true);
		} else {
			setIsLoading(false);
		}
	}, [isPlanShoppingListsLoading, isCompanyShoppingListsLoading, isPlanAdsLoading, isAuthLoading, plan]);

	const handleAddFavouriteFlashingBtnClick = () => {
		trackPixelCustomEvent('FavouriteButtonClicked', { planId: plan?.id });
		trackGAEvent({
			category: 'UI',
			action: 'FavouriteButtonClicked',
			label: 'planId',
			value: plan?.id,
		});
	};

	const shouldShowVisibilityOffAlert = plan?.publishedAt === null;
	const isPlanOwner = userProfile?.companyId === plan?.companyId;
	const shouldShowFlashingFavouriteIconButton = !userProfile && plan?.materialsCost;
	const shouldShowPasskeyAlert = plan?.password && isPlanOwner;

	// Generate SEO data
	const seoTitle = plan ? `${plan.alternativeTitle || plan.title} | DIYFeed` : 'DIY Project | DIYFeed';
	// Build localized, templated meta descriptions (avoid raw social text)
	const isSupplies = Boolean(isSuppliesTab);
	const seoDescription = isSupplies
		? t('seo.supplies')
		: t('seo.plan');
	const seoImage = plan?.images?.[0]?.imgUrl || '/og-image.jpg';
	const seoUrl = `/plans/${id}`;
	
	// Generate structured data
	const productSchema = plan ? generateProductSchema(plan) : null;
	const howToSchema = plan ? generateHowToSchema(plan) : null;
	const breadcrumbSchema = generateBreadcrumbSchema([
		{ name: 'Home', url: 'https://www.diyfeed.com/' },
		{ name: 'Plans', url: 'https://www.diyfeed.com/plans' },
		{ name: plan?.title || 'Project', url: `https://www.diyfeed.com/plans/${id}` }
	]);

	return <>
		<SEOHead
			title={seoTitle}
			description={seoDescription}
			image={seoImage}
			url={seoUrl}
			type="article"
			structuredData={productSchema ?? undefined}
		/>
		{howToSchema && (
			<script type="application/ld+json">
				{JSON.stringify(howToSchema)}
			</script>
		)}
		<script type="application/ld+json">
			{JSON.stringify(breadcrumbSchema)}
		</script>
		<PageWrapper isLoading={isLoading} error={planInfoPublicError || planInfoError} disableGutters>
			<Container>
				{shouldShowPasskeyAlert && <Box display='flex' justifyContent='center'>
					<Tooltip title={t('planView.passkeyInfo')}>
						<Alert sx={{borderRadius: 10, bgcolor: alpha(theme.palette.text.primary, 0.05), color: 'primary.main', '& .MuiAlert-icon': {color: 'primary.main'}}} icon={<LockIcon />}>
							<Typography variant='body1'>{t('planView.passKeyLabel')}</Typography>
						</Alert>
					</Tooltip>
				</Box>}
				{isPasswordRequired && <Box sx={{height: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
					<form onSubmit={handlePasswordSubmit}>
						<Box display='flex' flexDirection='column' gap={2} alignItems='center' sx={{width: '100%', maxWidth: 400}}>
							<Typography variant='h6'>{t('planView.passwordRequired')}</Typography>
							<TextField fullWidth label={t('planView.password')} type='password' name='password' required />
							<Button size='large' type='submit' variant='contained' color='primary' startIcon={<LockOpenIcon />}>{t('common.continue')}</Button>
						</Box>
					</form>
				</Box>}
				{plan?.id && <Box display='flex' alignItems='center'>
					<Typography variant='h2' sx={{flex:1, mt: 2, mb: 1}}>{plan.alternativeTitle || plan.title}</Typography>
					{!isPlanOwner && (<Box>
						{shouldShowFlashingFavouriteIconButton ?
							<FlashingFavouriteIconButtonWithRegisterTooltip onClick={handleAddFavouriteFlashingBtnClick} />
							:
							<IconButton
								size='large'
								onClick={() => favouriteIds.has(plan.id) ? handleRemoveFavouriteBtnClick(plan.id) : handleAddFavouriteBtnClick(plan)}
							>
								{favouriteIds.has(plan.id) ? <FavoriteIcon color='primary' /> : <FavoriteBorderIcon color='disabled' />}
							</IconButton>
						}
					</Box>)}
					{isPlanOwner && <Box>
						<IconButton size='large' component={Link} to={`/plans/${id}/edit`}>
							<EditIcon />
						</IconButton>
					</Box>}
				</Box>}
				{plan?.id && <Box sx={{ borderBottom: 1, borderColor: 'divider' }} ref={materialsListRef}>
					<Tabs value={isSuppliesTab ? 1 : 0} onChange={handleTabChange} aria-label="basic tabs example">
						<Tab label={t('planView.plan')} {...a11yProps(0)} />
						<Tab icon={<ShoppingCartIcon />} iconPosition='end' label={t('planView.supplies')} {...a11yProps(1)} sx={{minHeight: 48}} />
					</Tabs>
				</Box>}
			</Container>
			<CustomTabPanel value={isSuppliesTab ? 1 : 0} index={0}>
				{plan?.id && <PlanViewInfoTabContent plan={plan} quickOrderCosts={planShoppingLists.map((list) => ({materialsCost: list.materialsCost, materialsCostCurrency: list.currency}))} />}
			</CustomTabPanel>
			<CustomTabPanel value={isSuppliesTab ? 1 : 0} index={1}>
				{plan?.id && <PlanViewResourcesTabContent 
					planId={plan.id}
					isVendorsEnabled={plan.isVendorsEnabled}
					isAuthorVerified={plan.isVerifiedByAuthor}
					resources={plan.resources}
					planShoppingLists={planShoppingLists}
					companyShoppingList={companyShoppingList}
					planAds={planAds}
					companyPlanAd={companyPlanAd}
					handleFavouriteClick={() => setAddToWishlistPlanInfo(plan)}
				/>}
			</CustomTabPanel>
			{shouldShowVisibilityOffAlert && <Container sx={{mt: 2}}>
				<Grid2 size={{xs: 12}}>
					<Alert severity="info" icon={<VisibilityOffIcon />}>
						{t('planView.notPublished')}
					</Alert>
				</Grid2>
			</Container>}
			<WishlistDialog 
				open={!!addToWishlistPlanInfo}
				onClose={() => setAddToWishlistPlanInfo(null)}
				plan={addToWishlistPlanInfo}
			/>
			<Snackbar open={Boolean(errorMessage)} onClose={() => setErrorMessage(null)}>
				<Alert severity='error' sx={{width: '100%'}}>{errorMessage}</Alert>
			</Snackbar>
		</PageWrapper>
	</>;
};

export default PlanView;

